import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  updateDoc,
  doc,
  addDoc,
  deleteDoc,
  serverTimestamp,
  getDoc,
} from "firebase/firestore";
import { FaUser, FaEye, FaExternalLinkAlt, FaEnvelope } from "react-icons/fa";
import { toast } from 'react-toastify';
import { sendWelcomeEmail } from '../utils/emailService';

const DATE_RANGES = {
  day: () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d;
  },
  week: () => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d;
  },
  month: () => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d;
  },
  all: () => null,
};

function getColumns(entityKey) {
  // Simple columns for demo; customize as needed
  switch (entityKey) {
    case "users":
      return ["fullName", "email", "role", "status", "createdAt"];
    case "parents":
      return ["fullName", "email", "status", "createdAt"];
    case "professionals":
      return ["name", "email", "role", "city", "status", "createdAt"];
    case "organizations":
      return ["orgName", "email", "city", "status", "createdAt"];
    case "jobs":
      return ["title", "city", "status", "createdAt"];
    case "assessments":
      return ["userId", "score", "status", "createdAt"];
    default:
      return [];
  }
}

export default function EntityTable({ entityKey, collectionName, roleFilter, search, dateFilter }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editRow, setEditRow] = useState(null);
  const [formData, setFormData] = useState({});
  const [viewingProfile, setViewingProfile] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, [search, dateFilter]);

  const fetchRows = async () => {
    setLoading(true);
    setError("");
    try {
      let q = collection(db, collectionName);
      let constraints = [];
      if (roleFilter) constraints.push(where("role", "==", roleFilter));
      if (dateFilter && dateFilter !== "all") {
        const fromDate = DATE_RANGES[dateFilter]();
        if (fromDate) constraints.push(where("createdAt", ">=", fromDate));
      }
      if (constraints.length > 0) {
        q = query(q, ...constraints, orderBy("createdAt", "desc"));
      } else {
        q = query(q, orderBy("createdAt", "desc"));
      }
      const snap = await getDocs(q);
      let data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      if (search) {
        const lower = search.toLowerCase();
        data = data.filter(row =>
          Object.values(row).some(v => v && String(v).toLowerCase().includes(lower))
        );
      }
      setRows(data);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleStatusToggle = async (row) => {
    const newStatus = row.status === "active" ? "inactive" : "active";
    await updateDoc(doc(db, collectionName, row.id), { status: newStatus });
    fetchRows();
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Are you sure you want to delete this ${entityKey.slice(0, -1)}?`)) return;
    try {
      await deleteDoc(doc(db, collectionName, row.id));
      setRows(rows.filter(r => r.id !== row.id));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSendWelcomeEmail = async (row) => {
    if (!window.confirm(`Send welcome email to ${row.email || 'this user'}?`)) return;
    
    try {
      await sendWelcomeEmail({
        uid: row.id,
        email: row.email,
        displayName: row.displayName || row.fullName || row.name || 'User'
      });
      
      toast.success(`Welcome email sent to ${row.email}`);
    } catch (error) {
      console.error('Error sending welcome email:', error);
      const errorMessage = error.message || 'Failed to send welcome email';
      toast.error(errorMessage);
    }
  };

  const handleEdit = (row) => {
    setEditRow(row.id);
    setFormData(row);
  };

  const handleCancelEdit = () => {
    setEditRow(null);
    setFormData({});
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    const { id, ...rest } = formData;
    await updateDoc(doc(db, collectionName, id), rest);
    setEditRow(null);
    fetchRows();
  };

  const handleAdd = () => {
    setEditRow("new");
    setFormData({ status: "active", createdAt: new Date() });
  };

  const handleCreate = async () => {
    let data = { ...formData, createdAt: serverTimestamp() };
    if (roleFilter) data.role = roleFilter;
    await addDoc(collection(db, collectionName), data);
    setEditRow(null);
    fetchRows();
  };

  const handleViewProfile = async (userId, userType) => {
    setProfileLoading(true);
    setViewingProfile({ id: userId, type: userType });
    try {
      // First try the direct approach with the provided user type
      const collectionsToTry = [];
      
      // Add the primary collection based on user type
      if (userType) {
        collectionsToTry.push(
          userType === 'user' ? 'users' : 
          userType === 'professional' ? 'professionals' : 
          'organizations'
        );
      }
      
      // Also try other collections if the primary one fails
      if (userType !== 'user') collectionsToTry.push('users');
      if (userType !== 'professional') collectionsToTry.push('professionals');
      if (userType !== 'organization') collectionsToTry.push('organizations');
      
      // Remove duplicates while preserving order
      const uniqueCollections = [...new Set(collectionsToTry)];
      
      let profileFound = false;
      
      // Try each collection until we find the profile
      for (const collectionName of uniqueCollections) {
        try {
          const docRef = doc(db, collectionName, userId);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            setProfileData({ 
              id: docSnap.id, 
              ...docSnap.data(),
              _collection: collectionName // Store the collection where we found the profile
            });
            profileFound = true;
            break;
          }
        } catch (err) {
          console.warn(`Error checking ${collectionName} collection:`, err);
          // Continue to next collection
        }
      }
      
      if (!profileFound) {
        console.log(`Profile not found in any collection for user ID: ${userId}`);
        setError("Profile not found in the system");
        setViewingProfile(null);
      }
    } catch (err) {
      setError(err.message);
      setViewingProfile(null);
    }
    setProfileLoading(false);
  };

  const getDashboardModalType = (userType) => {
    switch (userType) {
      case 'parent':
        return 'parentDashboard';
      case 'professional':
        return 'professionalDashboard';
      case 'organization':
        return 'organizationDashboard';
      default:
        return null;
    }
  };

  const getUserType = (row) => {
    console.log('Getting user type for row:', { row, collectionName });
    
    // First check if the row has a role property
    if (row.role) {
      // Map 'therapist' to 'professional' since that's what the modal expects
      if (row.role === 'therapist') return 'professional';
      return row.role;
    }
    
    // If no role property, determine by collection name
    if (collectionName === 'users') {
      return 'parent'; // Default to parent for users collection
    } else if (collectionName === 'professionals' || collectionName === 'therapists') {
      return 'professional';
    } else if (collectionName === 'organizations') {
      return 'organization';
    }
    
    console.warn('Could not determine user type for row:', row);
    return 'user';
  };

  const cols = getColumns(entityKey);

  return (
    <div>
      <div className="flex justify-between mb-2">
        <button className="px-3 py-1 bg-blue-600 text-white rounded" onClick={handleAdd}>Add New</button>
        {loading && <span className="text-gray-400">Loading...</span>}
      </div>
      {error && <div className="text-red-500 mb-2">{error}</div>}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm border">
          <thead>
            <tr>
              {cols.map(col => <th key={col} className="px-2 py-1 border-b bg-gray-50 font-semibold text-left">{col}</th>)}
              <th className="px-2 py-1 border-b bg-gray-50"></th>
            </tr>
          </thead>
          <tbody>
            {editRow === "new" && (
              <tr>
                {cols.map(col => (
                  <td key={col} className="border px-2 py-1">
                    <input name={col} value={formData[col] || ""} onChange={handleChange} className="border rounded px-1 py-0.5 w-full" />
                  </td>
                ))}
                <td className="border px-2 py-1 flex gap-1">
                  <button className="bg-green-600 text-white px-2 py-1 rounded" onClick={handleCreate}>Save</button>
                  <button className="bg-gray-400 text-white px-2 py-1 rounded" onClick={handleCancelEdit}>Cancel</button>
                </td>
              </tr>
            )}
            {rows.map(row => (
              <tr key={row.id} className={row.status === "inactive" ? "bg-gray-100" : ""}>
                {cols.map(col => (
                  <td key={col} className="border px-2 py-1">
                    {editRow === row.id ? (
                      <input name={col} value={formData[col] || ""} onChange={handleChange} className="border rounded px-1 py-0.5 w-full" />
                    ) : col === "status" ? (
                      <button onClick={() => handleStatusToggle(row)} className={`px-2 py-1 rounded ${row.status === "active" ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-600"}`}>
                        {row.status === "active" ? "Active" : "Inactive"}
                      </button>
                    ) : col === "createdAt" ? (
                      row.createdAt?.toDate ? row.createdAt.toDate().toLocaleString() : (row.createdAt ? new Date(row.createdAt).toLocaleString() : "-")
                    ) : (
                      row[col] || "-"
                    )}
                  </td>
                ))}
                <td className="border px-2 py-1 flex gap-1">
                  {editRow === row.id ? (
                    <>
                      <button className="bg-green-600 text-white px-2 py-1 rounded" onClick={handleSave}>Save</button>
                      <button className="bg-gray-400 text-white px-2 py-1 rounded" onClick={handleCancelEdit}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <button 
                        className="bg-blue-500 text-white p-1 rounded" 
                        onClick={() => handleViewProfile(row.id, getUserType(row))}
                        title="View Profile"
                      >
                        <FaUser />
                      </button>
                      <button 
                        onClick={(e) => {
                          e.preventDefault();
                          const userType = getUserType(row);
                          console.log('Dashboard button clicked', { userType, rowId: row.id });
                          const modalType = getDashboardModalType(userType);
                          console.log('Modal type:', modalType);
                          if (modalType) {
                            console.log('Attempting to open modal...');
                            try {
                              if (window.parent && window.parent.openDashboardModal) {
                                console.log('Calling parent.openDashboardModal', { modalType, userId: row.id });
                                window.parent.openDashboardModal(modalType, row.id);
                              } else {
                                console.error('window.parent.openDashboardModal not found');
                              }
                            } catch (err) {
                              console.error('Error opening dashboard modal:', err);
                            }
                          } else {
                            console.error('No modal type found for user type:', userType);
                          }
                        }}
                        className="bg-green-500 text-white p-1 rounded inline-flex items-center justify-center"
                        title="View Dashboard"
                      >
                        <FaExternalLinkAlt size={12} />
                      </button>
                      <button 
                        className="bg-yellow-500 text-white p-1 rounded" 
                        onClick={() => handleEdit(row)}
                        title="Edit"
                      >
                        <FaEye />
                      </button>
                      <button 
                        className="bg-yellow-500 text-white p-1 rounded" 
                        onClick={() => handleSendWelcomeEmail(row)}
                        title="Send Welcome Email"
                      >
                        <FaEnvelope />
                      </button>
                      <button 
                        className="bg-red-500 text-white p-1 rounded" 
                        onClick={() => handleDelete(row)}
                        title="Delete"
                      >
                        ×
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && editRow !== "new" && (
              <tr><td colSpan={cols.length + 1} className="text-center text-gray-400 py-4">No records found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Profile View Modal */}
      {viewingProfile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">
                {viewingProfile.type === 'professional' ? 'Professional' : 
                 viewingProfile.type === 'organization' ? 'Organization' : 'User'} Profile
              </h3>
              <button 
                onClick={() => {
                  setViewingProfile(null);
                  setProfileData(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>
            
            <div className="p-4">
              {profileLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : profileData ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(profileData).map(([key, value]) => {
                      // Skip these fields or format them specially
                      if (['id', 'password', 'createdAt', 'updatedAt'].includes(key)) return null;
                      
                      let displayValue = value;
                      
                      // Format dates
                      if (value && (key.toLowerCase().includes('date') || key === 'createdAt' || key === 'updatedAt')) {
                        displayValue = value.toDate ? value.toDate().toLocaleString() : new Date(value).toLocaleString();
                      }
                      
                      // Handle nested objects
                      if (typeof value === 'object' && value !== null) {
                        displayValue = JSON.stringify(value, null, 2);
                      }
                      
                      return (
                        <div key={key} className="border-b pb-2">
                          <div className="text-xs text-gray-500 uppercase font-medium">
                            {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                          </div>
                          <div className="break-words">
                            {displayValue || '-'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="flex justify-end space-x-2 pt-4 border-t">
                    <button
                      onClick={() => {
                        const modalType = getDashboardModalType(viewingProfile.type);
                        if (modalType && window.parent && window.parent.openDashboardModal) {
                          window.parent.openDashboardModal(modalType, profileData.id);
                        }
                      }}
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center gap-2"
                    >
                      <FaExternalLinkAlt /> View Dashboard
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No profile data available
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
